import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useFinancial } from '../context/FinancialContext';

const { FiMap, FiCheck, FiLock, FiPieChart, FiShield, FiBriefcase, FiTrendingDown, FiUmbrella, FiTarget, FiActivity, FiLayers, FiUnlock, FiZap, FiRefreshCw, FiArrowRight, FiArrowLeft, FiInfo, FiHelpCircle, FiTrendingUp, FiDollarSign, FiClock, FiAlertTriangle, FiRepeat, FiAlertCircle, FiSettings, FiUser, FiHome, FiPercent, FiStar, FiFastForward, FiChevronDown, FiChevronUp } = FiIcons;

// --- SHARED DEBT TOOL ---
const DebtStrategyTool = () => {
  const [activeTab, setActiveTab] = useState('general');

  return (
    <div className="mt-8 space-y-6">
      <div className="flex gap-2 p-1.5 bg-slate-100 rounded-2xl">
        <button
          onClick={() => setActiveTab('general')}
          className={`flex-1 py-3 text-xs font-black uppercase tracking-widest rounded-xl transition-all ${activeTab === 'general' ? 'bg-white text-rose-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
        >
          General Debt
        </button>
        <button
          onClick={() => setActiveTab('mortgage')}
          className={`flex-1 py-3 text-xs font-black uppercase tracking-widest rounded-xl transition-all ${activeTab === 'mortgage' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
        >
          Mortgage & Home
        </button>
      </div>

      <div className="max-h-none md:max-h-[500px] md:overflow-y-auto custom-scrollbar pr-1 overflow-visible">
        {activeTab === 'general' && (
          <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2">
            <div className="p-8 bg-rose-50 rounded-[2.5rem] border border-rose-100">
              <h4 className="text-sm font-black text-rose-900 mb-6 flex items-center gap-2 uppercase tracking-widest"><SafeIcon icon={FiFastForward} className="text-xl" /> Interest Compression</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-5 bg-white rounded-2xl border border-rose-100 shadow-sm">
                  <p className="text-xs font-black text-rose-900 mb-2 uppercase">Month 1 Double Principal</p>
                  <p className="text-xs text-rose-700 leading-relaxed font-semibold">Making a double principal payment on month one targets the tail-end of the loan term where interest accumulation is most aggressive, triggering an exponential reduction in the total cost of debt.</p>
                </div>
                <div className="p-5 bg-white rounded-2xl border border-rose-100 shadow-sm">
                  <p className="text-xs font-black text-rose-900 mb-2 uppercase">Biweekly Schedule</p>
                  <p className="text-xs text-rose-700 leading-relaxed font-semibold">Switching to biweekly payments ensures you make 26 half-payments (13 full payments) per year. This mathematical hack reduces a 30-year term by roughly 6 years without lifestyle impact.</p>
                </div>
              </div>
              <div className="mt-6 p-5 bg-rose-900 text-white rounded-2xl">
                <h5 className="text-[10px] font-black uppercase tracking-widest mb-2 opacity-80">Standard Eradication</h5>
                <ul className="text-xs space-y-2 font-bold">
                  <li>• <span className="opacity-70">0% Balance Transfers:</span> Freeze interest for 12-21 months.</li>
                  <li>• <span className="opacity-70">The Avalanche:</span> Mathematically superior ROI by hitting highest rates first.</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'mortgage' && (
          <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2">
            <div className="p-8 bg-blue-50 rounded-[2.5rem] border border-blue-100">
              <h4 className="text-sm font-black text-blue-900 mb-6 flex items-center gap-2 uppercase tracking-widest"><SafeIcon icon={FiHome} className="text-xl" /> Acquisition & Optimization</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="p-5 bg-white rounded-2xl border border-blue-100 shadow-sm">
                    <p className="text-xs font-black text-blue-900 mb-2 uppercase">Negotiation Leverage</p>
                    <p className="text-xs text-blue-700 leading-relaxed font-semibold">Secure a lock-in quote, then approach competitors to beat it. Use this as leverage to waive appraisal fees and minimize origination costs.</p>
                  </div>
                  <div className="p-5 bg-white rounded-2xl border border-blue-100 shadow-sm">
                    <p className="text-xs font-black text-blue-900 mb-2 uppercase">Soft Check Strategy</p>
                    <p className="text-xs text-blue-700 leading-relaxed font-semibold">Utilize soft credit checks initially to explore market options without impacting your score. Finalize hard checks only within 120 days of purchase.</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="p-5 bg-blue-600 text-white rounded-2xl shadow-lg">
                    <p className="text-xs font-black mb-2 uppercase">Escrow Independence</p>
                    <p className="text-xs leading-relaxed font-bold">Close your escrow account after Month 2 to avoid lender opt-out fees ($300-$400). This allows you to manage taxes independently and earn interest on those funds in a HYSA.</p>
                  </div>
                  <div className="p-5 bg-emerald-50 rounded-2xl border border-emerald-100">
                    <p className="text-xs font-black text-emerald-900 mb-1 uppercase">Homestead Exemption</p>
                    <p className="text-xs text-emerald-700 font-bold leading-tight">Apply immediately to lower property taxes and cap future assessment increases.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Roadmap = () => {
  const { state, dispatch } = useFinancial();
  const [selectedStep, setSelectedStep] = useState('setup');
  const [hasEmployerMatch, setHasEmployerMatch] = useState(null);
  const [localExpenses, setLocalExpenses] = useState(state.calculations.monthlyExpenses || 2000);
  const [localInvested, setLocalInvested] = useState(state.calculations.monthlyInvested);
  const [localSpendingGoal, setLocalSpendingGoal] = useState(state.calculations.monthlyExpenses || 5000);
  
  // Mobile UI toggle for steps list
  const [mobileStepsExpanded, setMobileStepsExpanded] = useState(false);

  useEffect(() => {
    setLocalExpenses(state.calculations.monthlyExpenses || 2000);
    setLocalInvested(state.calculations.monthlyInvested);
  }, []);

  useEffect(() => {
    const firstIncomplete = ROADMAP_STEPS.find(step => !state.roadmapProgress.includes(step.id));
    if (firstIncomplete) setSelectedStep(firstIncomplete.id);
  }, []);

  const formatCurrency = (val) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);

  const ROADMAP_STEPS = [
    {
      id: 'setup',
      title: 'Strategic Baseline',
      subtitle: 'Step 1 • Parameters',
      icon: FiSettings,
      color: 'slate',
      description: 'Define your financial foundation. These parameters dictate the speed of your journey to independence. Pro Tip: Use conservative estimates for ROI and aggressive estimates for inflation to build a bulletproof plan.',
      renderTool: () => (
        <div className="mt-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">Current Age</label>
              <input type="number" value={state.goals.currentAge} onChange={(e) => dispatch({ type: 'UPDATE_GOALS', payload: { currentAge: parseInt(e.target.value) || 0 } })} className="w-full px-4 py-3 bg-slate-50 rounded-xl font-black text-slate-900 border-2 border-transparent focus:border-slate-900 outline-none transition-all" />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">Retirement Age</label>
              <input type="number" value={state.goals.targetAge} onChange={(e) => dispatch({ type: 'UPDATE_GOALS', payload: { targetAge: parseInt(e.target.value) || 0 } })} className="w-full px-4 py-3 bg-slate-50 rounded-xl font-black text-slate-900 border-2 border-transparent focus:border-slate-900 outline-none transition-all" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">Invested Capital</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-black">$</span>
                <input type="number" value={state.goals.startingNetWorth} onChange={(e) => dispatch({ type: 'UPDATE_GOALS', payload: { startingNetWorth: parseFloat(e.target.value) || 0 } })} className="w-full pl-8 pr-4 py-3 bg-slate-50 rounded-xl font-black text-slate-900 border-2 border-transparent focus:border-slate-900 outline-none transition-all" />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">Monthly Invested</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-black">$</span>
                <input type="number" value={localInvested} onChange={(e) => setLocalInvested(parseFloat(e.target.value) || 0)} className="w-full pl-8 pr-4 py-3 bg-slate-50 rounded-xl font-black text-slate-900 border-2 border-transparent focus:border-slate-900 outline-none transition-all" />
              </div>
            </div>
          </div>
          <div className="p-4 bg-blue-50 rounded-xl border border-blue-100 flex gap-3 items-start mt-4">
            <SafeIcon icon={FiInfo} className="text-blue-500 mt-0.5 flex-shrink-0" />
            <p className="text-[11px] font-bold text-blue-800 leading-tight">Pro Tip: Use conservative ROI (6-7%) and aggressive inflation (3%) to build a bulletproof plan.</p>
          </div>
        </div>
      )
    },
    {
      id: 'mini_ef',
      title: 'Starter Emergency Fund',
      subtitle: 'Step 2 • Starter Emergency Fund',
      icon: FiShield,
      color: 'indigo',
      description: 'Save a starter emergency fund of $1,000. This covers minor unexpected costs without going into debt while you tackle high-interest liabilities.',
      renderTool: () => (
        <div className="mt-8 p-6 bg-indigo-50 rounded-2xl border border-indigo-100 flex gap-4 items-start">
          <SafeIcon icon={FiAlertTriangle} className="text-indigo-500 mt-1 flex-shrink-0 text-2xl" />
          <div>
            <h4 className="text-xs font-black text-indigo-900 uppercase tracking-wider mb-2">Keep it Liquid</h4>
            <p className="text-xs font-bold text-indigo-800 leading-relaxed">Keep this $1,000 in a highly accessible checking or savings account. Do not invest it. The goal is immediate access during an emergency to prevent high-interest credit card debt.</p>
          </div>
        </div>
      ),
      dynamicTarget: 'Target: $1,000'
    },
    {
      id: 'match',
      title: 'Employer Match',
      subtitle: 'Step 3 • Free Money',
      icon: FiBriefcase,
      color: 'emerald',
      description: 'This is a guaranteed 100% return on your money. Grab the full employer match before investing anywhere else—it is the single highest ROI move in your portfolio. Never leave free money on the table.',
      renderTool: () => (
        <div className="mt-6 space-y-4">
          <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Do you have an employer match?</p>
          <div className="flex gap-2">
            {['Yes', 'No'].map(opt => (
              <button
                key={opt}
                onClick={() => setHasEmployerMatch(opt)}
                className={`flex-1 py-4 rounded-xl font-black text-xs uppercase tracking-widest transition-all ${hasEmployerMatch === opt ? 'bg-emerald-600 text-white shadow-lg' : 'bg-slate-50 text-slate-400 border border-slate-200 hover:bg-slate-100'}`}
              >
                {opt}
              </button>
            ))}
          </div>
          {hasEmployerMatch === 'Yes' && (
            <div className="p-5 bg-emerald-50 rounded-2xl border border-emerald-100 animate-in fade-in slide-in-from-top-2">
              <p className="text-xs text-emerald-800 leading-relaxed font-bold">
                Action: Contribute exactly enough to get the maximum match. No more, no less for now. Check your HR portal for the "Match Percentage" (e.g., 50% up to 6%).
              </p>
            </div>
          )}
        </div>
      )
    },
    {
      id: 'debt',
      title: 'Debt Eradication',
      subtitle: 'Step 4 • ROI Strategy',
      icon: FiTrendingDown,
      color: 'rose',
      description: 'Eradicate high-interest debt and optimize large loans using Month 1 double-principal and biweekly mathematical hacks. Note: For debts under 4% interest, mathematically it often makes more sense to invest extra cash instead.',
      renderTool: () => <DebtStrategyTool />
    },
    {
      id: 'full_ef',
      title: 'Full Emergency Fund',
      subtitle: 'Step 5 • Safety Net (Max)',
      icon: FiUmbrella,
      color: 'indigo',
      description: 'Build your safety net to 3-6 months of expenses. This prevents you from ever needing to touch your investments during a market downturn.',
      renderTool: () => (
        <div className="mt-8 space-y-6">
          <div className="space-y-1.5">
            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">Monthly Expenses</label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-black">$</span>
              <input type="number" value={localExpenses} onChange={(e) => setLocalExpenses(parseFloat(e.target.value) || 0)} className="w-full pl-8 pr-4 py-4 bg-slate-50 rounded-2xl font-black text-lg text-slate-900 border-2 border-transparent focus:border-indigo-500 outline-none transition-all" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">3 Month Floor</p>
              <p className="text-2xl font-black text-slate-900">{formatCurrency(localExpenses * 3)}</p>
            </div>
            <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">6 Month Target</p>
              <p className="text-2xl font-black text-slate-900">{formatCurrency(localExpenses * 6)}</p>
            </div>
          </div>
          <div className="p-5 bg-indigo-50 rounded-2xl border border-indigo-100 flex gap-3 items-start">
            <SafeIcon icon={FiShield} className="text-indigo-500 mt-0.5 flex-shrink-0 text-xl" />
            <p className="text-[11px] font-bold text-indigo-800 leading-tight">Note: If you have highly variable income (freelance, sales) or work in a volatile industry, aim for 6-12 months of runway instead of 3-6.</p>
          </div>
        </div>
      )
    },
    {
      id: 'fi_number',
      title: 'Calculate FI Number',
      subtitle: 'Step 6 • The Target',
      icon: FiTarget,
      color: 'purple',
      description: 'Financial Independence (FI) is the mathematical point where your invested assets generate enough yield to cover your living expenses forever. It is the bridge to true Financial Freedom—the ability to choose how you spend your time without being tied to a paycheck.',
      renderTool: () => {
        const annual = localSpendingGoal * 12;
        const fiTarget = annual * 25;
        const safeTarget = annual * 28;

        if (state.goals.targetAmount === 0 || !state.goals.targetAmount) {
          setTimeout(() => dispatch({ type: 'UPDATE_GOALS', payload: { targetAmount: fiTarget } }), 0);
        }

        return (
          <div className="mt-8 space-y-6">
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Desired Monthly Spending in Retirement</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-black">$</span>
                <input type="number" value={localSpendingGoal} onChange={(e) => setLocalSpendingGoal(parseFloat(e.target.value) || 0)} className="w-full pl-8 pr-4 py-4 bg-slate-50 rounded-2xl font-black text-lg text-slate-900 border-2 border-transparent focus:border-purple-500 outline-none transition-all" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => dispatch({ type: 'UPDATE_GOALS', payload: { targetAmount: fiTarget } })}
                className={`p-6 rounded-[2rem] text-left transition-all ${state.goals.targetAmount === fiTarget ? 'bg-purple-600 text-white shadow-xl shadow-purple-200 ring-4 ring-purple-500/20' : 'bg-slate-50 text-slate-600 hover:bg-purple-50 border-2 border-transparent'}`}
              >
                <p className="text-[10px] font-black uppercase tracking-widest opacity-70 mb-1">Standard FI (25x)</p>
                <p className="text-2xl font-black">{formatCurrency(fiTarget)}</p>
                <p className="text-[10px] font-bold mt-2 opacity-80">Withdraw {formatCurrency(annual)}/yr</p>
              </button>
              <button
                onClick={() => dispatch({ type: 'UPDATE_GOALS', payload: { targetAmount: safeTarget } })}
                className={`p-6 rounded-[2rem] text-left transition-all ${state.goals.targetAmount === safeTarget ? 'bg-purple-600 text-white shadow-xl shadow-purple-200 ring-4 ring-purple-500/20' : 'bg-white border-2 border-purple-100 text-purple-900 hover:bg-purple-50'}`}
              >
                <p className="text-[10px] font-black uppercase tracking-widest text-purple-400 mb-1">Conservative (28x)</p>
                <p className="text-2xl font-black">{formatCurrency(safeTarget)}</p>
                <p className="text-[10px] font-bold mt-2 text-purple-500">Lower 3.5% withdrawal rate</p>
              </button>
            </div>
          </div>
        );
      }
    },
    {
      id: 'req_savings',
      title: 'Strategic Alignment',
      subtitle: 'Step 7 • The Trajectory',
      icon: FiActivity,
      color: 'orange',
      description: 'Analyze the delta between your current velocity and the speed required to hit your target by your chosen retirement age.',
      renderTool: () => {
        if (!state.roadmapProgress.includes('setup')) {
          return (
            <div className="mt-8 p-8 bg-slate-50 rounded-3xl border border-slate-200 text-center flex flex-col items-center">
              <SafeIcon icon={FiAlertTriangle} className="text-4xl text-orange-500 mb-4" />
              <h4 className="text-base font-black text-slate-900 mb-2">Prerequisite Required</h4>
              <p className="text-sm text-slate-600 mb-6">This step requires your baseline financial parameters (like Invested Capital) to calculate your trajectory.</p>
              <button
                onClick={() => setSelectedStep('setup')}
                className="px-8 py-4 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-black transition-all shadow-xl"
              >
                Go to Step 1
              </button>
            </div>
          );
        }
        if (!state.roadmapProgress.includes('fi_number')) {
          return (
            <div className="mt-8 p-8 bg-slate-50 rounded-3xl border border-slate-200 text-center flex flex-col items-center">
              <SafeIcon icon={FiAlertTriangle} className="text-4xl text-purple-500 mb-4" />
              <h4 className="text-base font-black text-slate-900 mb-2">Target Required</h4>
              <p className="text-sm text-slate-600 mb-6">We need your calculated FI number from Step 6 to align your strategy.</p>
              <button
                onClick={() => setSelectedStep('fi_number')}
                className="px-8 py-4 bg-purple-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-purple-700 transition-all shadow-xl"
              >
                Go to Step 6
              </button>
            </div>
          );
        }

        const current = localInvested;
        const required = state.calculations.requiredMonthlyInvestment;
        const gap = required - current;
        const percentage = required > 0 ? Math.min(100, Math.max(0, (current / required) * 100)) : 100;
        const isSuccess = gap <= 0;

        const radius = 80;
        const circ = Math.PI * radius;
        const offset = circ - (percentage / 100) * circ;
        const rotation = -90 + (percentage / 100) * 180;
        const strokeColor = isSuccess ? '#10b981' : '#f97316';

        return (
          <div className="mt-8 space-y-4">
            <div className="flex justify-between items-end px-2">
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Current Savings Velocity</p>
                <p className="text-xl font-black text-slate-900">{formatCurrency(current)}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Required Savings Velocity</p>
                <p className="text-xl font-black text-blue-600">{formatCurrency(required)}</p>
              </div>
            </div>
            <div className={`p-6 rounded-[2rem] border-2 ${gap <= 0 ? 'bg-emerald-50 border-emerald-100' : 'bg-orange-50 border-orange-100'}`}>
              <div className="flex flex-col items-center">
                <p className={`font-black mb-6 ${gap <= 0 ? 'text-emerald-900' : 'text-orange-900'}`}>
                  {gap <= 0 ? 'Trajectory Confirmed' : `Gap Analysis: ${formatCurrency(gap)} / mo`}
                </p>
                <div className="relative w-full flex justify-center h-32 overflow-visible">
                  <svg className="w-full max-w-[280px] overflow-visible" viewBox="0 0 220 110">
                    <path d="M 30 85 A 80 80 0 0 1 190 85" fill="none" stroke="#e2e8f0" strokeWidth="14" strokeLinecap="round" />
                    <path
                      d="M 30 85 A 80 80 0 0 1 190 85"
                      fill="none"
                      stroke={strokeColor}
                      strokeWidth="14"
                      strokeLinecap="round"
                      strokeDasharray={circ}
                      strokeDashoffset={offset}
                      className="transition-all duration-1000 ease-out"
                    />
                    <g transform={`translate(110,85) rotate(${rotation})`} className="transition-transform duration-1000 ease-out">
                      <polygon points="-2.5,0 2.5,0 0,-60" fill="#64748b" />
                      <circle cx="0" cy="0" r="6" fill="#64748b" />
                      <circle cx="0" cy="0" r="2.5" fill="#ffffff" />
                    </g>
                    <text x="30" y="105" textAnchor="middle" fill="#94a3b8" fontSize="9" fontWeight="900">$0</text>
                    <text x="190" y="105" textAnchor="middle" fill="#94a3b8" fontSize="9" fontWeight="900" className="uppercase">Target: {formatCurrency(required)}</text>
                  </svg>
                </div>
                <div className="flex flex-col items-center mt-6 z-10 relative">
                  <span className={`text-4xl font-black tracking-tighter leading-none ${isSuccess ? 'text-emerald-600' : 'text-slate-900'}`}>{formatCurrency(current)}</span>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-2">Current Monthly Savings</span>
                </div>
                {!isSuccess && (
                  <p className="text-xs text-orange-700 mt-8 leading-relaxed font-medium text-center max-w-md">To bridge this gap, you must decrease spending, extend the timeline, or pull the income lever (Step 11).</p>
                )}
              </div>
            </div>
            <div className="p-5 bg-orange-50 rounded-2xl border border-orange-100 mt-4 flex gap-3 items-start">
              <SafeIcon icon={FiActivity} className="text-orange-500 mt-0.5 flex-shrink-0 text-xl" />
              <p className="text-[11px] font-bold text-orange-800 leading-tight">Remember: Consistency is more powerful than timing the market. Automate this monthly flow so you never have to think about it.</p>
            </div>
          </div>
        );
      }
    },
    {
      id: 'account_order',
      title: 'Tax Optimization',
      subtitle: 'Step 8 • Efficiency',
      icon: FiLayers,
      color: 'blue',
      description: 'Fill these tax-advantaged buckets in order. We recommend Fidelity for a unified brokerage experience due to their zero-fee index funds and superior HSA integration. Strategic tax placement can save you hundreds of thousands of dollars over your investing lifetime.',
      renderTool: () => (
        <div className="mt-8 space-y-4 text-left">
          {[
            { title: "1. Max HSA (If Eligible)", body: "The 'Triple Tax Advantage': Tax-deductible contributions, tax-free growth, and tax-free withdrawals for medical. It is the only account that avoids taxes at every stage.", tag: "Triple Advantage" },
            { title: "2. Max Roth IRA", body: "Contributions are after-tax, but ALL growth and withdrawals are tax-free. Standard deductions in retirement often mean these investments are never taxed again.", tag: "Tax-Free Growth" },
            { title: "3. Max Employer 401(k)", body: "Traditional 401(k) lowers your taxable income today. Earnings are taxed later, ideally at a lower bracket when you are no longer earning a salary.", tag: "Tax-Deferred" }
          ].map((acc, i) => (
            <div key={i} className="p-6 bg-white border border-slate-100 rounded-[1.5rem] shadow-sm hover:shadow-md transition-all group">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-black text-slate-900 text-sm">{acc.title}</h4>
                <span className="text-[8px] font-black uppercase px-2 py-1 bg-blue-50 text-blue-600 rounded-lg">{acc.tag}</span>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed font-medium">{acc.body}</p>
            </div>
          ))}
        </div>
      )
    },
    {
      id: 'bridge',
      title: 'Early Retirement Bridge',
      subtitle: 'Step 9 • Solving 59.5',
      icon: FiUnlock,
      color: 'teal',
      description: 'To retire before age 59.5, you need a strategy to access your tax-advantaged funds without 10% penalties. This allows you to "bridge" the gap between retirement and age 59.5. Warning: This requires a 5-year seasoning period, so planning must start 5 years before early retirement.',
      renderTool: () => (
        <div className="mt-8 space-y-4">
          <div className="p-6 bg-teal-50 border border-teal-100 rounded-[2rem]">
            <h4 className="font-black text-teal-900 text-sm mb-4 flex items-center gap-2">
              <SafeIcon icon={FiRepeat} className="text-teal-600" />
              The Roth Ladder Strategy
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <p className="text-[10px] font-black text-teal-600 uppercase tracking-widest">Strategic Value</p>
                <ul className="text-[11px] text-teal-800 space-y-2 font-bold">
                  <li className="flex gap-2"><SafeIcon icon={FiCheck} className="flex-shrink-0" /> Access 401k/IRA funds at any age</li>
                  <li className="flex gap-2"><SafeIcon icon={FiCheck} className="flex-shrink-0" /> Pay taxes in low-income years</li>
                  <li className="flex gap-2"><SafeIcon icon={FiCheck} className="flex-shrink-0" /> Tax-free principal after 5 years</li>
                </ul>
              </div>
              <div className="space-y-3">
                <p className="text-[10px] font-black text-rose-600 uppercase tracking-widest">Constraints</p>
                <ul className="text-[11px] text-rose-800 space-y-2 font-bold">
                  <li className="flex gap-2"><SafeIcon icon={FiInfo} className="flex-shrink-0" /> Requires 5 years of cash buffer</li>
                  <li className="flex gap-2"><SafeIcon icon={FiInfo} className="flex-shrink-0" /> Conversion counts as taxable income</li>
                  <li className="flex gap-2"><SafeIcon icon={FiInfo} className="flex-shrink-0" /> Complex record keeping required</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="p-6 bg-slate-50 border border-slate-100 rounded-[1.5rem]">
            <h4 className="font-black text-slate-900 text-sm mb-2 flex items-center gap-2">
              <SafeIcon icon={FiArrowRight} className="text-slate-400" />
              Backdoor Roth Alternative
            </h4>
            <p className="text-xs text-slate-600 leading-relaxed font-medium">
              If your income exceeds Roth IRA limits, you contribute to a Traditional IRA and immediately convert it. This is a vital "side door" for high-velocity wealth builders.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'income_lever',
      title: 'The Income Lever',
      subtitle: 'Step 10 • Acceleration',
      icon: FiZap,
      color: 'amber',
      description: 'If your current trajectory is too slow, you need to pull the income lever. This is the fastest way to compress a 20-year timeline into 10 years. Remember: You can only cut expenses to a certain point, but your income potential is theoretically limitless.',
      renderTool: () => {
        if (!state.roadmapProgress.includes('req_savings')) {
          return (
            <div className="mt-8 p-8 bg-slate-50 rounded-3xl border border-slate-200 text-center flex flex-col items-center">
              <SafeIcon icon={FiAlertTriangle} className="text-4xl text-amber-500 mb-4" />
              <h4 className="text-base font-black text-slate-900 mb-2">Prerequisite Required</h4>
              <p className="text-sm text-slate-600 mb-6">We need to analyze your trajectory in Step 7 before we can calculate your income gap.</p>
              <button
                onClick={() => setSelectedStep('req_savings')}
                className="px-8 py-4 bg-amber-500 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-amber-600 transition-all shadow-xl"
              >
                Go to Step 7
              </button>
            </div>
          );
        }
        const gap = state.calculations.requiredMonthlyInvestment - localInvested;
        return (
          <div className="mt-8 space-y-6">
            <div className="p-8 bg-gradient-to-br from-amber-500 to-orange-600 rounded-[2.5rem] text-white shadow-xl shadow-amber-200">
              <p className="text-[10px] font-black uppercase tracking-widest opacity-80 mb-2">Required Additional Monthly Savings</p>
              <p className="text-4xl font-black tracking-tighter">{formatCurrency(Math.max(0, gap))}</p>
              <p className="text-xs font-bold mt-4 opacity-90 leading-relaxed">
                {gap > 0 ? `You need to find an additional ${formatCurrency(gap)} in monthly savings or income to hit your FI goal on schedule.` : "You have no savings gap! Any additional income purely accelerates your FI date."}
              </p>
            </div>
          </div>
        );
      }
    },
    {
      id: 'checkpoint',
      title: 'Annual Checkpoint',
      subtitle: 'Step 11 • Maintenance',
      icon: FiRefreshCw,
      color: 'slate',
      description: 'Financial independence is a marathon. Re-run these numbers every 12 months to account for life changes, inflation, and market performance.',
      renderTool: () => (
        <div className="mt-8 p-6 bg-slate-50 rounded-2xl border border-slate-200 flex gap-4 items-start">
          <SafeIcon icon={FiRefreshCw} className="text-slate-500 mt-1 flex-shrink-0 text-2xl" />
          <div>
            <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider mb-2">Beware Lifestyle Creep</h4>
            <p className="text-xs font-bold text-slate-600 leading-relaxed">As your income grows, your expenses naturally tend to follow. Reviewing your numbers annually ensures your FI target stays accurate to your current lifestyle.</p>
          </div>
        </div>
      ),
      dynamicTarget: 'Target: Yearly Trajectory Review'
    }
  ];

  const activeStepDetails = ROADMAP_STEPS.find(s => s.id === selectedStep) || ROADMAP_STEPS[0];
  const isSelectedComplete = state.roadmapProgress.includes(selectedStep);

  const toggleStep = (id) => {
    dispatch({ type: 'TOGGLE_ROADMAP_STEP', payload: id });
    if (!state.roadmapProgress.includes(id)) {
      const currentIndex = ROADMAP_STEPS.findIndex(s => s.id === id);
      if (currentIndex < ROADMAP_STEPS.length - 1) {
        setSelectedStep(ROADMAP_STEPS[currentIndex + 1].id);
      }
    }
  };

  const progressPercentage = (state.roadmapProgress.length / ROADMAP_STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />

      <main className="max-w-[1600px] mx-auto p-4 md:p-6 space-y-6">
        <div className="bg-white rounded-[2.5rem] p-8 md:p-10 border border-slate-100 shadow-sm flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center shadow-inner">
              <SafeIcon icon={FiMap} className="text-3xl" />
            </div>
            <div>
              <h1 className="text-3xl font-black text-slate-900 tracking-tighter">
                <span className="md:hidden">Financial</span>
                <span className="hidden md:inline">Financial Roadmap</span>
              </h1>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mt-1">Strategic Path to Freedom</p>
            </div>
          </div>
          <div className="w-full md:w-80">
            <div className="flex justify-between items-end mb-3">
              <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Journey Completion</span>
              <span className="text-sm font-black text-blue-600">{Math.round(progressPercentage)}%</span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden p-0.5 shadow-inner">
              <motion.div
                className="bg-gradient-to-r from-blue-600 to-indigo-600 h-full rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progressPercentage}%` }}
                transition={{ duration: 1, ease: "easeOut" }}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left Column: Milestones List */}
          <div className="lg:col-span-4 lg:sticky lg:top-28">
            <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm p-6 lg:p-8">
              <button
                onClick={() => setMobileStepsExpanded(!mobileStepsExpanded)}
                className="w-full flex items-center justify-between lg:mb-10 lg:ml-2 focus:outline-none cursor-pointer lg:cursor-default"
              >
                <h2 className="text-[11px] font-black uppercase text-slate-400 tracking-[0.2em]">Milestones</h2>
                <SafeIcon icon={mobileStepsExpanded ? FiChevronUp : FiChevronDown} className="lg:hidden text-slate-400 text-lg" />
              </button>
              
              <div className={`relative pl-6 space-y-10 mt-8 lg:mt-0 before:absolute before:inset-0 before:ml-[1.15rem] before:w-0.5 before:bg-slate-100 before:z-0 ${mobileStepsExpanded ? 'block' : 'hidden lg:block'}`}>
                {ROADMAP_STEPS.map((step, index) => {
                  const isComplete = state.roadmapProgress.includes(step.id);
                  const isSelected = selectedStep === step.id;
                  let statusColor = isComplete ? 'emerald' : isSelected ? 'blue' : 'slate';
                  let StatusIcon = isComplete ? FiCheck : isSelected ? step.icon : FiLock;

                  return (
                    <div
                      key={step.id}
                      className="relative z-10 cursor-pointer group"
                      onClick={() => {
                        setSelectedStep(step.id);
                        setMobileStepsExpanded(false);
                      }}
                    >
                      <div className={`absolute -left-[35px] top-0 w-8 h-8 rounded-full border-4 border-white flex items-center justify-center transition-all duration-300 ${isSelected ? `bg-${statusColor}-600 scale-125 shadow-xl shadow-${statusColor}-200` : `bg-${statusColor}-100`}`}>
                        <SafeIcon icon={StatusIcon} className={`text-[10px] ${isSelected ? 'text-white' : `text-${statusColor}-500`}`} />
                      </div>
                      <div className={`pl-4 transition-all duration-300 ${isSelected ? 'translate-x-2' : 'group-hover:translate-x-1'}`}>
                        <p className={`text-[9px] font-black uppercase tracking-widest mb-1 ${isComplete ? 'text-emerald-500' : isSelected ? 'text-blue-500' : 'text-slate-400'}`}>Step {index + 1}</p>
                        <h3 className={`text-sm font-black tracking-tight ${isSelected ? 'text-slate-900' : 'text-slate-600'}`}>{step.title}</h3>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right Column: Step Details */}
          <div className="lg:col-span-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStepDetails.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden min-h-[700px] flex flex-col"
              >
                <div className={`h-64 w-full bg-gradient-to-br from-${activeStepDetails.color}-50 to-slate-50 relative flex items-center justify-center`}>
                  <div className={`absolute inset-0 bg-grid-slate-200/50 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))]`} />
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className={`w-28 h-28 bg-white rounded-[2rem] shadow-2xl shadow-${activeStepDetails.color}-200/50 flex items-center justify-center relative z-10 border border-white`}
                  >
                    <SafeIcon icon={activeStepDetails.icon} className={`text-6xl text-${activeStepDetails.color}-500`} />
                  </motion.div>
                </div>

                <div className="p-10 md:p-14 flex-1 flex flex-col">
                  <div className="max-w-2xl">
                    <p className={`text-[10px] font-black uppercase tracking-widest text-${activeStepDetails.color}-500 mb-3`}>{activeStepDetails.subtitle}</p>
                    <h2 className="text-4xl font-black text-slate-900 tracking-tighter mb-6">{activeStepDetails.title}</h2>
                    <p className="text-slate-600 text-base leading-relaxed font-medium">{activeStepDetails.description}</p>
                  </div>

                  <div className="flex-1">
                    {activeStepDetails.renderTool ? activeStepDetails.renderTool() : (
                      <div className="mt-10 bg-slate-50 rounded-[2rem] p-6 border border-slate-100 flex items-center gap-6">
                        <div className="w-14 h-14 bg-white rounded-2xl shadow-sm flex items-center justify-center flex-shrink-0">
                          <SafeIcon icon={FiTarget} className="text-2xl text-slate-400" />
                        </div>
                        <div>
                          <p className="text-[8px] font-black uppercase text-slate-400 mb-0.5">Dynamic Milestone</p>
                          <p className="text-xl font-black text-slate-900 tracking-tight">{activeStepDetails.dynamicTarget}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-12 flex flex-col sm:flex-row gap-4">
                    <button
                      onClick={() => {
                        const idx = ROADMAP_STEPS.findIndex(s => s.id === selectedStep);
                        if (idx > 0) setSelectedStep(ROADMAP_STEPS[idx - 1].id);
                      }}
                      className="flex-1 py-6 rounded-2xl font-black text-xs uppercase tracking-widest text-slate-500 bg-slate-50 hover:bg-slate-100 border border-slate-200 flex items-center justify-center gap-2 transition-all"
                    >
                      <SafeIcon icon={FiArrowLeft} /> Back
                    </button>
                    <button
                      onClick={() => toggleStep(selectedStep)}
                      className={`flex-[2] py-6 rounded-2xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-3 transition-all ${isSelectedComplete ? 'bg-slate-100 text-slate-500 hover:bg-slate-200' : 'bg-slate-900 text-white hover:bg-black shadow-2xl shadow-slate-200'}`}
                    >
                      <SafeIcon icon={FiCheck} className="text-xl" />
                      {isSelectedComplete ? 'Mark as Incomplete' : 'Complete This Step'}
                    </button>
                    <button
                      onClick={() => {
                        const idx = ROADMAP_STEPS.findIndex(s => s.id === selectedStep);
                        if (idx < ROADMAP_STEPS.length - 1) setSelectedStep(ROADMAP_STEPS[idx + 1].id);
                      }}
                      className="flex-1 py-6 rounded-2xl font-black text-xs uppercase tracking-widest text-slate-500 bg-slate-50 hover:bg-slate-100 border border-slate-200 flex items-center justify-center gap-2 transition-all"
                    >
                      Skip <SafeIcon icon={FiArrowRight} />
                    </button>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Roadmap;